import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { IActivity, IMotif } from '@leedpulse-monorepo/utils';

type AiReceptionist = { prompt: string; first_message: string; activities?: IActivity[]; motifs?: IMotif[] };

export default class Domain {
  private _id: string;
  private _name: string;
  private _description: string;
  private _tags: string[];
  private _positions: string[];
  private _networks: string[];
  private _created: Date;
  private _updated: Date;
  private _ai_receptionist: AiReceptionist;
  private _archived: boolean;

  constructor(
    id: string,
    name: string,
    description: string,
    tags: string[],
    positions: string[],
    networks: string[],
    created: Date,
    updated: Date,
    ai_receptionist: AiReceptionist,
    archived: boolean
  ) {
    this._id = id;
    this._name = name;
    this._description = description;
    this._tags = tags;
    this._positions = positions;
    this._networks = networks;
    this._created = created;
    this._updated = updated;
    this._ai_receptionist = ai_receptionist;
    this._archived = archived;
  }

  get id(): string {
    return this._id;
  }

  get name(): string {
    return this._name;
  }

  get description(): string {
    return this._description;
  }

  get tags(): string[] {
    return this._tags;
  }

  get positions(): string[] {
    return this._positions;
  }

  get networks(): string[] {
    return this._networks;
  }

  get created(): Date {
    return this._created;
  }

  get updated(): Date {
    return this._updated;
  }

  get ai_receptionist(): AiReceptionist {
    return this._ai_receptionist;
  }

  get archived(): boolean {
    return this._archived;
  }

  public copyWith({
    name,
    description,
    tags,
    positions,
    networks,
    created,
    updated,
    ai_receptionist,
    archived,
  }: {
    name?: string;
    description?: string;
    tags?: string[];
    positions?: string[];
    networks?: string[];
    created?: Date;
    updated?: Date;
    ai_receptionist?: AiReceptionist;
    archived?: boolean;
  }): Domain {
    return new Domain(
      this._id,
      name ?? this._name,
      description ?? this._description,
      tags ?? this._tags,
      positions ?? this._positions,
      networks ?? this._networks,
      created ?? this._created,
      updated ?? this._updated,
      ai_receptionist ?? this._ai_receptionist,
      archived ?? this._archived
    );
  }

  static generateDomainId(): string {
    const uuidBase = uuidv4().toUpperCase();

    return (
      'domain-' + uuidBase.substring(uuidBase.length - 10, uuidBase.length)
    );
  }

  static newEmpty(): Domain {
    return new Domain(
      Domain.generateDomainId(),
      '',
      '',
      [],
      [],
      [],
      new Date(),
      new Date(),
      { prompt: '', first_message: '' },
      false
    );
  }

  static fromFirestore(doc: QueryDocumentSnapshot<DocumentData>): Domain {
    const docData = doc.data();
    return new Domain(
      doc.id,
      docData.name,
      docData.description,
      docData.tags ?? [],
      docData.positions ?? [],
      docData.networks ?? [],
      new Date(docData.created),
      new Date(docData.updated),
      docData.ai_receptionist ?? { prompt: '', prompt_legal_entity: '', first_message: '' },
      docData.archived ?? false
    );
  }

  toFirestorePatch(): Record<string, any> {
    // We automatically update the updated field
    return {
      name: this._name,
      description: this._description,
      tags: this._tags,
      positions: this._positions,
      networks: this._networks,
      ai_receptionist: this._ai_receptionist,
      archived: this._archived,
      updated: new Date().getTime(),
    };
  }

  toFirestore(): Record<string, any> {
    return {
      id: this._id,
      name: this._name,
      description: this._description,
      tags: this._tags,
      positions: this._positions,
      networks: this._networks,
      ai_receptionist: this._ai_receptionist,
      archived: this._archived,
      created: this._created.getTime(),
      updated: this._updated.getTime(),
    };
  }
}
